import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { ArrowRight, Menu, X, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { FaInstagram, FaLinkedin, FaDribbble } from 'react-icons/fa';
import { SiUpwork } from 'react-icons/si';
import { Helmet } from 'react-helmet-async';
import { BLOG_POSTS } from './data/blogPosts';
import CurvedLoop from './CurvedLoop';
import Threads from './Threads';

const SEO = ({ title, description, url, type = "website" }) => {
  const image = "https://rayronastudio.com/logo192.png"; 
  return (
    <Helmet>
      <title>{title}</title>
      {url && <link rel="canonical" href={url} />}
      <meta name="description" content={description} />
      
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};
// --- STYLES & FONTS ---
const globalStyles = `
  :root {
    --bg-primary: #080808;
    --text-primary: #F4F4F5;
    --accent: #E85E31;
    --accent-hover: #D14D24;
  }

  body {
    background-color: var(--bg-primary);
    color: var(--text-primary);
    font-family: 'DM Sans', sans-serif;
    margin: 0;
    -webkit-font-smoothing: antialiased;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Syne', sans-serif;
  }

  .font-syne { font-family: 'Syne', sans-serif; }
  .font-georgia { font-family: 'Georgia', serif; font-style: italic; }
  .font-dmsans { font-family: 'DM Sans', sans-serif; }
  
  .text-accent { color: var(--accent); }
  .bg-accent { background-color: var(--accent); }
  .hover-bg-accent:hover { background-color: var(--accent-hover); }

  /* Custom selection color */
  ::selection {
    background-color: var(--accent);
    color: white;
  }

  /* Animasi teks berjalan (marquee) */
  @keyframes scroll-marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .animate-marquee {
    animation: scroll-marquee 50s linear infinite;
  }

  /* Reveal word animation */
  @keyframes reveal-word-up {
    from {
      transform: translateY(115%);
    }
    to {
      transform: translateY(0);
    }
  }

  .reveal-word {
    display: inline-block;
    transform: translateY(115%);
    animation: reveal-word-up 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    will-change: transform;
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
    .reveal-word {
      transform: none !important;
    }
  }

  /* Premium floating organic ambient glow background */
  @keyframes ambient-glow {
    0%, 100% {
      transform: scale(1) translate(0px, 0px);
      opacity: 0.5;
    }
    33% {
      transform: scale(1.15) translate(30px, -20px);
      opacity: 0.7;
    }
    66% {
      transform: scale(0.9) translate(-20px, 20px);
      opacity: 0.4;
    }
  }
  .animate-ambient {
    animation: ambient-glow 10s ease-in-out infinite;
  }
`;

// --- ANIMATION COMPONENTS ---
const FadeIn = ({ children, delay = 0, className = "" }) => {
  const [isVisible, setVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const currentRef = domRef.current;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (currentRef) observer.unobserve(currentRef);
        }
      });
    }, { threshold: 0.1 });

    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  return (
    <div
      ref={domRef}
      className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// --- MOCK DATA ---
const PORTFOLIO_ITEMS = [
  { id: 1, title: "Othello Pawcare", category: "E-Commerce & WordPress", img: "/othello.jpg", slug: "othello-pawcare" },
  { id: 2, title: "Squad Agency", category: "UI/UX Design", img: "/squad.png", slug: "squad-agency" },
  { id: 3, title: "Serenity Dashboard", category: "React Web App", img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800", slug: "serenity-dashboard" },
  { id: 4, title: "Echo Social", category: "Vibe System", img: "https://images.unsplash.com/photo-1618761714954-0b8cd0026356?auto=format&fit=crop&q=80&w=800", slug: "echo-social" },
];



const PRIVACY_POLICIES = [
  {
    id: "monflow",
    title: "MonFlow - Track Money Flow",
    date: "July 3, 2026",
    excerpt: "Privacy policy for the MonFlow - Track Money Flow offline, free, and accountless financial tracking application.",
    content: "Welcome to MonFlow - Track Money Flow, a modern, simple, and 100% free offline financial tracking application. This Privacy Policy explains our practices regarding data collection, storage, and privacy when you use our mobile application (the \"App\"). This policy is designed to comply with global privacy standards, including Google Play Store and Apple App Store compliance guidelines.\n\nAt MonFlow, your privacy is our highest priority. By design, MonFlow operates entirely offline and requires no user registration, no login, and no internet connection for its core financial features.\n\n1. No Account and No Personal Data Collection\nMonFlow is built with a strict privacy-first architecture. We do NOT require you to create an account, register, or sign in:\na. No Registration or Credential Collection: We do not ask for, collect, transmit, or store any personally identifiable information (PII) such as your name, email address, phone number, username, or passwords.\nb. No Banking Credentials: MonFlow is a standalone personal financial ledger. We never request, access, or connect to your real bank accounts, credit cards, or online banking PINs.\nc. Free to Use: MonFlow is completely free. We do not charge subscription fees or collect payment details.\n\n2. Local Data Storage (100% Offline Architecture)\nAll financial records you create in MonFlow are stored strictly on your own device:\n- On-Device SQLite & Local DB: When you input income, expenses, budgets, category names, or notes, this data is written directly to your device's internal local database.\n- No Cloud Synchronization: We do NOT maintain cloud servers or remote databases to store your financial data. Your financial records never leave your physical device.\n- Full Data Ownership: Because your data resides solely on your device, you maintain 100% ownership and control over your financial records at all times.\n\n3. Third-Party Services and Platform Diagnostic Data\nWhile MonFlow operates offline, the mobile operating system and distribution platforms (such as Google Play Store) may collect standard anonymous technical data:\n- Platform Analytics & Error Reporting: In the event of an app crash or technical error, standard anonymous diagnostic logs (such as OS version, device model, and crash traces) may be processed by Google Play Services or basic platform tools to help us fix bugs and improve app stability.\n- No Ad Tracking / No Data Selling: We do NOT monetize, sell, rent, or share any user data with third-party data brokers or marketing agencies. Link to platform privacy policy: Google Play Services (https://policies.google.com/privacy).\n\n4. User Data Management and Deletion Rights\nBecause MonFlow does not store your data on external servers, you have immediate and complete control over data deletion without needing to submit a request to us:\n- In-App Data Reset: You can clear or reset all your financial records and transaction history directly within the app settings.\n- Device Uninstall: Uninstalling or clearing the storage of the MonFlow app from your device will permanently and irreversibly erase all local database records associated with the app.\n- No Remote Retention: Since we never hold a copy of your data on cloud servers, once you delete the app or clear its data locally, your records are completely erased.\n\n5. Data Security\nEven though your data is stored locally on your device, we design MonFlow following industry-standard software coding best practices to ensure the local database remains secure within your operating system's application sandbox. We recommend protecting your physical device with a passcode, biometrics, or device encryption to prevent unauthorized physical access to your local financial ledger.\n\n6. Children's Privacy\nMonFlow is a safe, offline financial management tool suitable for general audiences of all ages. Because we do not collect any personal data or require user registration, we do not knowingly collect or maintain personally identifiable information from anyone, including children under the age of 13.\n\n7. Changes to This Privacy Policy\nWe may update our Privacy Policy from time to time to reflect app updates or regulatory requirements. We advise you to review this page periodically for any changes. We will notify you of any changes by posting the new Privacy Policy on this page with an updated revision date. Changes are effective immediately upon posting.\n\n8. Contact Us\nIf you have any questions, suggestions, or feedback regarding this Privacy Policy or the offline architecture of MonFlow, please feel free to contact us at codeveloper16@gmail.com or visit our website at https://rayronastudio.com/."
  },
  {
    id: "rayrona-studio",
    title: "Rayrona Studio General",
    date: "October 2025",
    excerpt: "General privacy policy for Rayrona Studio website and services.",
    content: "Welcome to Rayrona Studio. Your privacy is important to us. This privacy policy explains how we collect, use, and protect your personal information when you use our website.\n\n1. Information We Collect\nWe only collect information that you voluntarily provide to us, such as your name and email address when you contact us through our forms or sign up for our newsletter. We may also automatically collect standard analytics data (like IP addresses and browser types) to improve our website experience.\n\n2. How We Use Your Information\nThe information we collect is used strictly to respond to your inquiries, provide the services requested, and improve the user experience on the Rayrona Studio platform. We do not sell or share your data with third parties for marketing purposes.\n\n3. Tracking & Cookies\nWe use basic cookies to ensure the website functions correctly and to analyze site traffic. You can adjust your browser settings to disable cookies, though this may affect the performance of some features on our site."
  },
  {
    id: "wordiva",
    title: "Wordiva - Word Puzzle Game",
    date: "April 25, 2026",
    excerpt: "Privacy policy for the Wordiva Android application.",
    content: "Welcome to Wordiva, a word puzzle game. This Privacy Policy explains how we collect, use, and disclose information about you when you use our mobile application (the \"App\"). This policy is designed to comply with Google Play Store app compliance standards.\n\nWe use your data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this policy.\n\n1. Information Collection and Use\nTo provide the best experience while using our Service, we may ask you to provide us with certain personally identifiable information. The information that we request will be retained by us and used as described in this privacy policy.\na. Account Data (Login Feature): Our app features authentication (login). When you register and use the Service, we may collect personal information such as Email Address, Username, Password (stored securely and encrypted), and Profile picture (if you use third-party login options, such as Google Sign-In). This information is used solely to authenticate you, secure your account, and save your progress or preferences within the Wordiva app.\nb. Usage Data (Log Data): Whenever you use our Service, or in the case of an error in the app, we collect data and information (through third-party products) on your phone called Log Data. This Log Data may include information such as your device Internet Protocol (\"IP\") address, device name, operating system version, the configuration of the app when utilizing our Service, the time and date of your use of the Service, and other statistics.\n\n2. Third-Party Services (Including AdMob Ads)\nThe app does use third-party services that may collect information used to identify you to provide app features and serve advertisements.\nGoogle AdMob (Advertising): We use Google AdMob to display advertisements within the Wordiva app. Our advertising partners, including Google, may use a Mobile Advertising ID (such as Google Advertising ID on Android) and cookies to serve personalized ads based on users' interests and previous interaction history.\nTo learn more about how Google uses your data, please visit: Google Privacy Policy (https://policies.google.com/privacy).\nHow to Opt-Out: Users can opt-out of personalized advertising by visiting Google Settings on your Android device > Google > Ads, and enabling \"Opt out of Ads Personalization\" (or Delete advertising ID).\nLink to the privacy policy of third-party service providers used by the app: Google Play Services (https://policies.google.com/privacy), Google AdMob (https://support.google.com/admob/answer/6128543?hl=en), and Google Analytics for Firebase.\n\n3. Cookies and Tracking Technologies\nCookies are files with a small amount of data that are commonly used as anonymous unique identifiers. These are sent to your browser/device from the websites or services that you use and are stored on your device's internal memory.\nThe Wordiva app does not use these \"cookies\" explicitly. However, the app may use third-party code and libraries (such as AdMob) that use \"cookies\" to collect information, serve ads, and improve their services.\n\n4. Account and User Data Deletion\nIn accordance with Google Play policies, we grant you the right to delete your account and personal data from our systems.\nYou can request the deletion of your account and associated data through the \"Delete Account\" option in the Settings menu within the Wordiva app. Alternatively, you can contact us via email at codeveloper16@gmail.com to request manual data deletion. Once the request is received, your authentication data will be permanently deleted from our servers.\n\n5. Data Security\nWe value your trust in providing us your Personal Information, thus we are striving to use commercially acceptable means of protecting it. But remember that no method of transmission over the internet, or method of electronic storage is 100% secure and reliable, and we cannot guarantee its absolute security.\n\n6. Children's Privacy\nOur Services are accessible to all ages, but they are not specifically targeted at children under the age of 13. We do not knowingly collect personally identifiable information directly from children under 13 for marketing purposes without permission. In the case we discover that a child under 13 has provided us with personal information unintentionally, we immediately delete this from our servers. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us.\n\n7. Changes to This Privacy Policy\nWe may update our Privacy Policy from time to time. Thus, you are advised to review this page periodically for any changes. We will notify you of any changes by posting the new Privacy Policy on this page. These changes are effective immediately after they are posted.\n\n8. Contact Us\nIf you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at codeveloper16@gmail.com or visit our website at https://rayronastudio.com/."
  }
];


// --- MAIN APPLICATION COMPONENT ---
export default function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navigateTo = (page, data = null) => {
    if (page === 'home') navigate('/');
    if (page === 'blog') navigate('/blog');
    if (page === 'post') navigate(`/blog/${data.slug}`);
    if (page === 'privacy_list') navigate('/privacy');
    if (page === 'privacy_detail') navigate(`/privacy/${data.id}`);
    if (page === 'delete_account') navigate('/delete-account');
  };

  const getActiveTab = () => {
    if (location.pathname.startsWith('/blog')) return 'blog';
    if (location.pathname.startsWith('/privacy')) return 'privacy';
    return 'home';
  };
  const currentPage = getActiveTab();

  // -- PAGE COMPONENTS --
  const Home = () => (
    <div className="pt-32 pb-24 animate-in fade-in duration-500">
      <SEO 
        title="Rayrona Studio | Designing the vibe, coding the future" 
        description="Portfolio of Rayrona Studio. I'm a multidisciplinary Vibe Designer & Coder bridging the gap between high-end aesthetic systems and robust technical execution." 
        url="https://rayronastudio.com/"
      />
      {/* Hero Section */}
      <section className="relative w-full mb-32 text-center flex flex-col items-center justify-center min-h-[70vh] overflow-visible">
        {/* Background Threads Animation */}
        <div className="absolute inset-0 -z-20 opacity-30 w-full h-full">
          <Threads
            amplitude={1.2}
            distance={0.3}
            enableMouseInteraction={true}
            color={[1, 1, 1]}
          />
        </div>

        {/* Ambient glow background */}
        <div className="absolute inset-0 -z-10 pointer-events-none flex items-center justify-center">
          <div className="w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(232,94,49,0.08)_0%,rgba(232,94,49,0)_70%)] blur-3xl animate-ambient"></div>
        </div>

        <div className="container mx-auto px-6 md:px-12 flex flex-col items-center justify-center">
          <h1 className="relative z-10 text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.05] sm:leading-[1.08] md:leading-[1.12] lg:leading-[1.15] max-w-5xl tracking-tight mx-auto text-center">
            <span className="inline-block align-bottom overflow-hidden pb-2 -mb-2">
              <span className="reveal-word" style={{ animationDelay: '150ms' }}>Designing</span>
            </span>
            {' '}
            <span className="inline-block align-bottom overflow-hidden pb-2 -mb-2">
              <span className="reveal-word" style={{ animationDelay: '250ms' }}>the</span>
            </span>
            {' '}
            <span className="inline-block align-bottom overflow-hidden pb-2 -mb-2">
              <span className="reveal-word font-georgia text-accent font-normal" style={{ animationDelay: '350ms' }}>vibe</span>
            </span>
            <span className="inline-block align-bottom overflow-hidden pb-2 -mb-2">
              <span className="reveal-word" style={{ animationDelay: '400ms' }}>,</span>
            </span>
            <br className="hidden md:inline" />
            {' '}
            <span className="inline-block align-bottom overflow-hidden pb-2 -mb-2">
              <span className="reveal-word" style={{ animationDelay: '550ms' }}>coding</span>
            </span>
            {' '}
            <span className="inline-block align-bottom overflow-hidden pb-2 -mb-2">
              <span className="reveal-word" style={{ animationDelay: '650ms' }}>the</span>
            </span>
            {' '}
            <span className="inline-block align-bottom overflow-hidden pb-2 -mb-2">
              <span className="reveal-word font-syne" style={{ animationDelay: '750ms' }}>future.</span>
            </span>
          </h1>

          <FadeIn delay={950} className="relative z-10">
            <p className="mt-8 text-xl md:text-2xl max-w-2xl text-gray-400 font-dmsans mx-auto text-center leading-relaxed">
              I'm a multidisciplinary <span className="text-white font-medium">Vibe Designer & Coder</span> bridging the gap between high-end aesthetic systems and robust technical execution.
            </p>
          </FadeIn>

          <FadeIn delay={1150} className="relative z-10">
            <div className="mt-12 flex justify-center gap-4">
              <button
                onClick={() => document.getElementById('work').scrollIntoView({ behavior: 'smooth' })}
                className="bg-accent hover-bg-accent text-white px-8 py-4 rounded-full font-medium transition-all duration-300 transform hover:-translate-y-1 shadow-lg shadow-orange-500/20 flex items-center gap-2"
              >
                View My Work <ArrowRight size={18} />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Vibe & Code Stack Marquee */}
      <section className="w-full overflow-visible mb-32 relative">
        <FadeIn delay={200}>
          <CurvedLoop 
            marqueeText="DESIGN ✦ CODE ✦ CREATE ✦ INSPIRE ✦ BUILD ✦ REPEAT ✦ "
            speed={2}
            curveAmount={150}
            direction="left"
            interactive={true}
            className="font-syne font-extrabold fill-zinc-800 hover:fill-white transition-colors duration-300"
          />
        </FadeIn>
      </section>

      {/* Bagian Tentang Saya & Tools */}
      <section id="about" className="container mx-auto px-6 md:px-12 mb-20">
        <FadeIn>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-widest text-accent mb-6">About Me</h2>
              <h3 className="text-4xl md:text-5xl font-bold leading-tight">
                Crafting interfaces with <span className="font-georgia text-accent font-normal">logic</span> and emotion.
              </h3>
            </div>

            <div className="lg:pt-14">
              <p className="text-lg md:text-xl text-gray-400 font-dmsans leading-relaxed">
                As the creator behind Rayrona Studio, I blend technical expertise with aesthetic design sensibilities. I believe that exceptional digital experiences shouldn't just function perfectly, but must also carry a "vibe" that captivates users.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 mb-32 relative z-10">
        <div className="container mx-auto px-6 md:px-12 mb-16">
          <FadeIn>
            <div className="max-w-4xl">
              <h2 className="text-sm font-bold uppercase tracking-widest text-accent mb-6">Services</h2>
              <h3 className="text-4xl md:text-5xl font-bold leading-tight">
                High-end solutions for <span className="font-georgia text-accent font-normal">digital</span> growth.
              </h3>
            </div>
          </FadeIn>
        </div>

        {/* Full-width lines and list */}
        <div className="w-full border-t border-zinc-800/60">
          {/* Service 1 */}
          <div className="border-b border-zinc-800/60 hover:bg-zinc-900/20 transition-all duration-500">
            <div className="container mx-auto px-6 md:px-12">
              <FadeIn delay={100}>
                <div className="group py-12 md:py-16 flex flex-col md:flex-row md:items-center justify-between gap-6 cursor-pointer">
                  <div className="flex items-center gap-6 md:gap-12">
                    <span className="text-accent text-sm md:text-base font-mono font-bold">01</span>
                    <h4 className="text-3xl md:text-5xl lg:text-6xl font-bold font-syne uppercase tracking-tight text-white group-hover:text-accent transition-colors duration-300">
                      UI/UX Design
                    </h4>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between md:justify-end gap-6 md:gap-16 w-full md:w-auto">
                    <p className="text-zinc-400 font-dmsans leading-relaxed max-w-md text-sm md:text-base group-hover:text-zinc-300 transition-colors duration-300">
                      Crafting functional, intuitive, and emotionally resonant interface systems optimized for engagement and conversion.
                    </p>
                    <div className="flex-shrink-0 w-12 h-12 rounded-full border border-zinc-800 group-hover:border-accent group-hover:bg-accent text-zinc-400 group-hover:text-white transition-all duration-300 flex items-center justify-center">
                      <ArrowUpRight className="w-6 h-6 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>

          {/* Service 2 */}
          <div className="border-b border-zinc-800/60 hover:bg-zinc-900/20 transition-all duration-500">
            <div className="container mx-auto px-6 md:px-12">
              <FadeIn delay={200}>
                <div className="group py-12 md:py-16 flex flex-col md:flex-row md:items-center justify-between gap-6 cursor-pointer">
                  <div className="flex items-center gap-6 md:gap-12">
                    <span className="text-accent text-sm md:text-base font-mono font-bold">02</span>
                    <h4 className="text-3xl md:text-5xl lg:text-6xl font-bold font-syne uppercase tracking-tight text-white group-hover:text-accent transition-colors duration-300">
                      WordPress Dev
                    </h4>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between md:justify-end gap-6 md:gap-16 w-full md:w-auto">
                    <p className="text-zinc-400 font-dmsans leading-relaxed max-w-md text-sm md:text-base group-hover:text-zinc-300 transition-colors duration-300">
                      Developing custom, highly performant Gutenberg themes and WooCommerce stores tailored to your exact business requirements.
                    </p>
                    <div className="flex-shrink-0 w-12 h-12 rounded-full border border-zinc-800 group-hover:border-accent group-hover:bg-accent text-zinc-400 group-hover:text-white transition-all duration-300 flex items-center justify-center">
                      <ArrowUpRight className="w-6 h-6 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>

          {/* Service 3 */}
          <div className="border-b border-zinc-800/60 hover:bg-zinc-900/20 transition-all duration-500">
            <div className="container mx-auto px-6 md:px-12">
              <FadeIn delay={300}>
                <div className="group py-12 md:py-16 flex flex-col md:flex-row md:items-center justify-between gap-6 cursor-pointer">
                  <div className="flex items-center gap-6 md:gap-12">
                    <span className="text-accent text-sm md:text-base font-mono font-bold">03</span>
                    <h4 className="text-3xl md:text-5xl lg:text-6xl font-bold font-syne uppercase tracking-tight text-white group-hover:text-accent transition-colors duration-300">
                      Logo & Branding
                    </h4>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between md:justify-end gap-6 md:gap-16 w-full md:w-auto">
                    <p className="text-zinc-400 font-dmsans leading-relaxed max-w-md text-sm md:text-base group-hover:text-zinc-300 transition-colors duration-300">
                      Designing memorable visual identifiers, logomarks, typography hierarchies, and branding guidelines to command attention.
                    </p>
                    <div className="flex-shrink-0 w-12 h-12 rounded-full border border-zinc-800 group-hover:border-accent group-hover:bg-accent text-zinc-400 group-hover:text-white transition-all duration-300 flex items-center justify-center">
                      <ArrowUpRight className="w-6 h-6 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>


      {/* Selected Works Section */}
      <section id="work" className="container mx-auto px-6 md:px-12">
        <FadeIn>
          <div className="flex justify-between items-end mb-12">
            <h2 className="text-4xl md:text-5xl font-bold">Selected Works</h2>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {PORTFOLIO_ITEMS.map((item, index) => (
            <FadeIn key={item.id} delay={index * 100}>
              <Link to={`/work/${item.slug}`} className="block group relative overflow-hidden rounded-2xl cursor-pointer bg-zinc-900 aspect-[4/3]" onClick={() => window.scrollTo(0, 0)}>
                <img
                  src={item.img}
                  alt={item.title}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchpriority={index === 0 ? "high" : "auto"}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-8">
                  <p className="text-white/80 font-dmsans text-sm mb-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">{item.category}</p>
                  <h3 className="text-white text-2xl font-bold font-syne transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75">{item.title}</h3>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>
      </section>
    </div>
  );

  const BlogList = () => {
    const query = new URLSearchParams(useLocation().search);
    const page = parseInt(query.get("page") || "1", 10);
    const postsPerPage = 6;
    const totalPages = Math.ceil(BLOG_POSTS.length / postsPerPage);
    const startIndex = (page - 1) * postsPerPage;
    const currentPosts = BLOG_POSTS.slice(startIndex, startIndex + postsPerPage);

    return (
    <div className="pt-32 pb-24 min-h-screen animate-in fade-in duration-500 container mx-auto px-6 md:px-12">
      <SEO 
        title={`Blog ${page > 1 ? `- Page ${page} ` : ''}| Rayrona Studio`} 
        description="Artikel seputar desain, vibe coding, dan eksplorasi Artificial Intelligence oleh Rayrona Studio." 
        url={`https://rayronastudio.com/blog${page > 1 ? `?page=${page}` : ''}`}
      />
      <FadeIn>
        <h1 className="text-5xl md:text-7xl font-bold mb-6">Rayrona <span className="font-georgia text-accent font-normal">Blog</span></h1>
        <p className="text-xl text-gray-400 mb-16 max-w-2xl">Artikel seputar desain, vibe coding, dan eksplorasi Artificial Intelligence.</p>
      </FadeIn>

      <div className="grid gap-12 max-w-4xl mb-16">
        {currentPosts.map((post, index) => (
          <FadeIn key={post.id} delay={index * 100}>
            <Link
              to={`/blog/${post.slug}`}
              className="block group cursor-pointer border-b border-zinc-800 pb-10"
            >
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="w-full md:w-1/3 aspect-[4/3] rounded-2xl overflow-hidden bg-zinc-900 flex-shrink-0">
                  <img src={post.img} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="w-full md:w-2/3">
                  <p className="text-sm text-zinc-400 mb-3">{post.date}</p>
                  <h2 className="text-3xl font-bold mb-4 group-hover:text-accent transition-colors duration-300">{post.title}</h2>
                  <p className="text-gray-400 text-lg leading-relaxed">{post.excerpt}</p>
                  <div className="mt-6 flex items-center text-accent font-medium gap-2 opacity-0 group-hover:opacity-100 transform -translate-x-4 group-hover:translate-x-0 transition-all duration-300">
                    Read Article <ArrowRight size={16} />
                  </div>
                </div>
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>

      {totalPages > 1 && (
        <FadeIn>
          <div className="flex justify-between items-center max-w-4xl pt-8">
            {page > 1 ? (
              <Link to={`/blog?page=${page - 1}`} className="flex items-center gap-2 font-medium hover:text-accent transition-colors text-zinc-400">
                <ArrowLeft size={18} /> Newer Posts
              </Link>
            ) : <div />}
            
            <span className="text-zinc-400 text-sm">Page {page} of {totalPages}</span>
            
            {page < totalPages ? (
              <Link to={`/blog?page=${page + 1}`} className="flex items-center gap-2 font-medium hover:text-accent transition-colors text-zinc-400">
                Older Posts <ArrowRight size={18} />
              </Link>
            ) : <div />}
          </div>
        </FadeIn>
      )}
    </div>
  );
  };

  const BlogPost = () => {
    const { slug } = useParams();
    const post = BLOG_POSTS.find(p => p.slug === slug);
    if (!post) return <div className="pt-32 text-center text-2xl font-bold">Post not found</div>;

    const relatedPosts = BLOG_POSTS.filter(p => p.id !== post.id).slice(0, 3);

    return (
      <div className="pt-32 pb-24 min-h-screen animate-in fade-in duration-500 container mx-auto px-6 md:px-12">
        <SEO 
          title={`${post.title} | Blog - Rayrona Studio`}
          description={post.excerpt}
          url={`https://rayronastudio.com/blog/${post.slug}`}
          type="article"
        />
        <FadeIn>
          <Link
            to="/blog"
            className="flex items-center gap-2 text-zinc-400 hover:text-accent transition-colors mb-12 font-medium"
          >
            <ArrowLeft size={18} /> Back to Blog
          </Link>
        </FadeIn>

        <article className="max-w-3xl mx-auto">
          <FadeIn delay={100}>
            <p className="text-accent font-medium tracking-widest uppercase text-sm mb-4">{post.date}</p>
            <h1 className="text-4xl md:text-6xl font-bold mb-12 leading-tight">{post.title}</h1>
            <div className="w-full aspect-[21/9] rounded-3xl overflow-hidden bg-zinc-900 mb-12">
              <img src={post.img} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </FadeIn>

          <FadeIn delay={200}>
            <div className="prose prose-lg text-gray-300 leading-relaxed font-dmsans">
              {post.content.split('\n\n').map((paragraph, idx) => {
                if (paragraph.startsWith('### ')) {
                  return <h3 key={idx} className="text-2xl font-bold text-white mt-8 mb-4">{paragraph.replace('### ', '')}</h3>;
                }
                if (paragraph.startsWith('## ')) {
                  return <h2 key={idx} className="text-3xl font-bold text-white mt-12 mb-6">{paragraph.replace('## ', '')}</h2>;
                }
                return <p key={idx} className="mb-6">{paragraph}</p>;
              })}
            </div>
          </FadeIn>
        </article>

        {/* Related Posts Section */}
        {relatedPosts.length > 0 && (
          <div className="max-w-4xl mx-auto mt-24 pt-16 border-t border-zinc-800">
            <h3 className="text-3xl font-bold mb-8">Related <span className="font-georgia text-accent font-normal">Posts</span></h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost, index) => (
                <FadeIn key={relatedPost.id} delay={index * 100}>
                  <Link to={`/blog/${relatedPost.slug}`} className="block group cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                    <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden bg-zinc-900 mb-4">
                      <img src={relatedPost.img} alt={relatedPost.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <p className="text-xs text-zinc-400 mb-2">{relatedPost.date}</p>
                    <h4 className="text-xl font-bold group-hover:text-accent transition-colors duration-300 leading-tight">{relatedPost.title}</h4>
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const PrivacyList = () => (
    <div className="pt-32 pb-24 min-h-screen animate-in fade-in duration-500 container mx-auto px-6 md:px-12">
      <SEO 
        title="Privacy Policies - Rayrona Studio" 
        description="A collection of privacy policies for Rayrona Studio and our digital products." 
        url="https://rayronastudio.com/privacy"
      />
      <FadeIn>
        <h1 className="text-5xl md:text-7xl font-bold mb-6">Privacy <span className="font-georgia text-accent font-normal">Policies</span></h1>
        <p className="text-xl text-gray-400 mb-16 max-w-2xl">A collection of privacy policies for Rayrona Studio and our digital products.</p>
      </FadeIn>

      <div className="grid gap-12 max-w-4xl">
        {PRIVACY_POLICIES.map((policy, index) => (
          <FadeIn key={policy.id} delay={index * 100}>
            <Link
              to={`/privacy/${policy.id}`}
              className="block group cursor-pointer border-b border-zinc-800 pb-10"
            >
              <p className="text-sm text-zinc-400 mb-3">{policy.date}</p>
              <h2 className="text-3xl font-bold mb-4 group-hover:text-accent transition-colors duration-300">{policy.title}</h2>
              <p className="text-gray-400 text-lg leading-relaxed">{policy.excerpt}</p>
              <div className="mt-6 flex items-center text-accent font-medium gap-2 opacity-0 group-hover:opacity-100 transform -translate-x-4 group-hover:translate-x-0 transition-all duration-300">
                Read Policy <ArrowRight size={16} />
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>
    </div>
  );

  const PrivacyDetail = () => {
    const { id } = useParams();
    const policy = PRIVACY_POLICIES.find(p => p.id === id);
    if (!policy) return <div className="pt-32 text-center text-2xl font-bold">Policy not found</div>;

    return (
      <div className="pt-32 pb-24 min-h-screen animate-in fade-in duration-500 container mx-auto px-6 md:px-12">
        <SEO 
          title={`${policy.title} - Privacy Policy`}
          description={policy.excerpt}
          url={`https://rayronastudio.com/privacy/${policy.id}`}
        />
        <FadeIn>
          <Link
            to="/privacy"
            className="flex items-center gap-2 text-zinc-400 hover:text-accent transition-colors mb-12 font-medium"
          >
            <ArrowLeft size={18} /> Back to Policies
          </Link>
        </FadeIn>

        <article className="max-w-3xl mx-auto">
          <FadeIn delay={100}>
            <p className="text-accent font-medium tracking-widest uppercase text-sm mb-4">Last Updated: {policy.date}</p>
            <h1 className="text-4xl md:text-6xl font-bold mb-12 leading-tight">{policy.title}</h1>
          </FadeIn>

          <FadeIn delay={200}>
            <div className="prose prose-lg text-gray-300 leading-relaxed font-dmsans">
              {policy.content.split('\n\n').map((paragraph, idx) => {
                if (paragraph.match(/^\d+\./)) {
                  const parts = paragraph.split('\n');
                  return (
                    <div key={idx}>
                      <h2 className="text-2xl font-bold text-white mt-8 mb-4">{parts[0]}</h2>
                      {parts.slice(1).map((p, i) => <p key={i} className="mb-4">{p}</p>)}
                    </div>
                  );
                }
                return <p key={idx} className="mb-6">{paragraph}</p>;
              })}
              <p className="pt-8 text-sm text-zinc-400 border-t border-zinc-800 mt-12">For any questions regarding this policy, please reach out via our contact links in the footer.</p>
            </div>
          </FadeIn>
        </article>
      </div>
    );
  };

  const DeleteAccount = () => {
    return (
      <div className="pt-32 pb-24 min-h-screen animate-in fade-in duration-500 container mx-auto px-6 md:px-12">
        <SEO 
          title="Delete Your Account - Rayrona Studio"
          description="How to request deletion of your account and personal data from Rayrona Studio applications."
          url="https://rayronastudio.com/delete-account"
        />
        <FadeIn>
          <Link
            to="/"
            className="flex items-center gap-2 text-zinc-400 hover:text-accent transition-colors mb-12 font-medium"
          >
            <ArrowLeft size={18} /> Back to Home
          </Link>
        </FadeIn>

        <article className="max-w-3xl mx-auto">
          <FadeIn delay={100}>
            <h1 className="text-4xl md:text-6xl font-bold mb-12 leading-tight">Delete Your <span className="font-georgia text-accent font-normal">Account</span></h1>
          </FadeIn>

          <FadeIn delay={200}>
            <div className="prose prose-lg text-gray-300 leading-relaxed font-dmsans space-y-6">
              <p>We respect your privacy and provide you with the ability to request deletion of your account and all associated personal data.</p>
              
              <h2 className="text-2xl font-bold text-white mt-10 mb-4">How to Delete Your Account</h2>
              <p>You can delete your account using one of the following methods:</p>
              
              <h3 className="text-xl font-bold text-white mt-6 mb-2">1. Directly in the App</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Open the app or game</li>
                <li>Go to Settings</li>
                <li>Select Delete Account and follow the instructions</li>
              </ul>
              
              <h3 className="text-xl font-bold text-white mt-6 mb-2">2. Request via Email</h3>
              <p>If you are unable to access the app, you can request account deletion by contacting us:</p>
              <p className="bg-zinc-900 p-4 rounded-lg flex items-center gap-2 font-medium mt-2">
                <span>📧</span> codeveloper16@gmail.com
              </p>
              <p className="mt-4">Please include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The app or game name</li>
                <li>Your account email or user ID</li>
              </ul>
              
              <h2 className="text-2xl font-bold text-white mt-10 mb-4">Processing Time</h2>
              <p>Account deletion requests are typically processed immediately or within 24 hours.</p>
              
              <h2 className="text-2xl font-bold text-white mt-10 mb-4">What Happens After Deletion</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your account will be permanently deleted</li>
                <li>All associated data will be removed from our systems</li>
                <li>This action cannot be undone</li>
              </ul>
              
              <h2 className="text-2xl font-bold text-white mt-10 mb-4">Applies to All Apps & Games</h2>
              <p>This process applies to all applications and games developed by Rayrona Studio. When submitting a request, please make sure to specify the relevant app or game.</p>
              
              <h2 className="text-2xl font-bold text-white mt-10 mb-4">Contact</h2>
              <p>If you have any questions, feel free to contact us at:</p>
              <p className="bg-zinc-900 p-4 rounded-lg flex items-center gap-2 font-medium mt-2">
                <span>📧</span> codeveloper16@gmail.com
              </p>
            </div>
          </FadeIn>
        </article>
      </div>
    );
  };

  const CaseStudy = () => {
    const { slug } = useParams();
    
    if (slug === 'othello-pawcare') {
      return (
        <div className="pt-32 pb-24 min-h-screen animate-in fade-in duration-500 container mx-auto px-6 md:px-12">
          <SEO 
            title="Othello Pawcare Case Study | Rayrona Studio"
            description="A deep dive into the design and development of the Othello Pawcare e-commerce website."
            url="https://rayronastudio.com/work/othello-pawcare"
            type="article"
          />
          <FadeIn>
            <Link
              to="/"
              onClick={() => setTimeout(() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' }), 100)}
              className="flex items-center gap-2 text-zinc-400 hover:text-accent transition-colors mb-12 font-medium w-fit"
            >
              <ArrowLeft size={18} /> Back to Work
            </Link>
          </FadeIn>

          <article className="max-w-4xl mx-auto">
            <FadeIn delay={100}>
              <p className="text-accent font-medium tracking-widest uppercase text-sm mb-4">E-Commerce & WordPress</p>
              <h1 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">Othello <span className="font-georgia text-accent font-normal">Pawcare</span></h1>
              <div className="w-full rounded-3xl overflow-hidden bg-zinc-900 mb-16 shadow-xl border border-zinc-800">
                <img src="/othello.jpg" alt="Othello Pawcare Website" loading="lazy" decoding="async" className="w-full object-cover" />
              </div>
            </FadeIn>

            <FadeIn delay={200}>
              <div className="prose prose-lg text-gray-300 leading-relaxed font-dmsans max-w-none">
                
                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Project Overview</h2>
                <p className="mb-6">Othello Pawcare is a comprehensive pet care service and e-commerce platform dedicated to the health and happiness of dogs and cats. They provide premium pet grooming, vaccination services, and a curated selection of high-quality animal snacks.</p>
                <p className="mb-12">The goal of this project was to establish a digital presence that immediately communicates trust, warmth, and professionalism. Pet owners need to feel absolutely confident before handing over their beloved companions or purchasing food for them.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 my-16">
                  <div className="bg-orange-950/20 p-8 rounded-2xl border border-orange-900/30">
                    <h3 className="text-xl font-bold text-white mb-4">The Challenge</h3>
                    <p className="text-base text-gray-300">Combining service bookings (grooming, vaccination) with standard e-commerce functionality (buying snacks) into a seamless, unified user journey without confusing the customer.</p>
                  </div>
                  <div className="bg-zinc-900/50 p-8 rounded-2xl border border-zinc-800">
                    <h3 className="text-xl font-bold text-white mb-4">The Solution</h3>
                    <p className="text-base text-gray-300">A clean, emotionally resonant "vibe design" approach focusing on high-quality imagery of happy pets, soft rounded UI elements, and a robust WordPress/WooCommerce backend.</p>
                  </div>
                </div>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Phase 1: Designing the Vibe in Figma</h2>
                <p className="mb-6">The visual identity was crucial. I started in Figma, opting for an off-white background to avoid the clinical sterility of pure white. The primary accent color was a warm, inviting orange, complementing the playful nature of pets.</p>
                <p className="mb-6">Typography choices included soft serif accents for elegance paired with highly readable sans-serif fonts for service details. I designed custom vector paw prints to serve as subtle background textures, reinforcing the brand identity without cluttering the interface.</p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Phase 2: Architectural Planning & UX Research</h2>
                <p className="mb-6">Before writing a single line of code, we needed to deeply understand the psychology of pet owners. When a user is looking for pet grooming or vaccination services, their primary emotional driver is anxiety. They want to ensure their pet is in safe, professional hands. This psychological insight drove the entire architectural planning phase. We conducted several user interviews to determine what information was most critical to them when landing on a pet care site. The results were clear: transparent pricing, verified customer reviews, and high-quality imagery of clean facilities and happy animals were non-negotiable.</p>
                <p className="mb-6">Armed with this data, we mapped out the user journey. We created a site architecture that minimized the number of clicks required to book an appointment. Instead of burying the booking form deep within a contact page, we integrated it directly into the service pages using a sticky call-to-action (CTA) that follows the user as they scroll. This seemingly small architectural decision drastically increased the conversion rate during our initial A/B testing.</p>
                
                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Phase 3: The E-Commerce Strategy</h2>
                <p className="mb-6">Integrating an e-commerce platform alongside service bookings presents a unique challenge. How do you prevent the user from being overwhelmed by choices? We tackled this by creating distinct, yet visually unified, zones within the application. The 'Snack Shop' was designed to feel like a boutique aisle within the larger clinic environment. We categorized the snacks not just by animal type, but by dietary needs—such as 'Grain-Free', 'Dental Health', and 'Anxiety Relief'.</p>
                <p className="mb-6">The shopping cart experience was also heavily optimized. We implemented a slide-out cart drawer instead of a traditional cart page redirect. This allows users to add items to their cart without losing their place on the page, significantly reducing cart abandonment rates. Furthermore, we integrated a seamless one-page checkout process, streamlining the collection of shipping and payment information.</p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Phase 4: WordPress & Custom WooCommerce Integration</h2>
                <p className="mb-6">After finalizing the high-fidelity prototypes, development transitioned into WordPress. We chose WordPress not for its out-of-the-box features, but for its flexibility when used as a foundational CMS framework. Instead of relying on bloated, pre-built themes that often suffer from performance issues and security vulnerabilities, I engineered a completely custom theme from scratch. This ensured that the codebase exactly matched the Figma designs and contained zero unnecessary legacy code.</p>
                <p className="mb-6">I integrated WooCommerce for the pet snack shop, customizing the PHP templates and overriding default WooCommerce styles to ensure the product pages felt like an organic part of the overall brand rather than a disconnected, generic storefront. Every button, input field, and product card was meticulously styled using custom CSS to maintain the 'vibe' established in the design phase.</p>
                <p className="mb-6">To empower the client, Advanced Custom Fields (ACF) Pro was utilized extensively. I built custom Gutenberg blocks and intuitive backend interfaces so the Othello Pawcare team could easily update service pricing, add new staff members, and manage the clinic's blog without ever needing to touch a line of code or worry about breaking the layout.</p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Phase 5: Performance Optimization & Accessibility</h2>
                <p className="mb-6">A premium design is meaningless if the website takes ten seconds to load. Performance optimization was treated as a core feature, not an afterthought. We implemented aggressive server-side caching and utilized a Content Delivery Network (CDN) to serve static assets globally. All images were compressed and served in the next-gen WebP format, drastically reducing the overall page weight. We also employed lazy loading for images outside the initial viewport, ensuring the critical rendering path was unblocked.</p>
                <p className="mb-6">Accessibility was another major focal point. We ensured that the color contrast ratios between text and background colors met the WCAG 2.1 AA standards, guaranteeing that visually impaired users could easily read the content. Semantic HTML structure was strictly adhered to, allowing screen readers to navigate the site logically. Focus states for all interactive elements were explicitly defined, providing a smooth experience for keyboard-only users. Building an accessible site is not just about compliance; it's about demonstrating empathy for all potential customers.</p>

                <div className="mt-16 pt-12 border-t border-zinc-800 text-center">
                  <h3 className="text-2xl font-bold text-white mb-6">Ready to see the live project?</h3>
                  <a 
                    href="https://othellopawcare.com/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-block bg-accent hover-bg-accent text-white px-8 py-4 rounded-full font-medium transition-all duration-300 transform hover:-translate-y-1 shadow-lg shadow-orange-500/20"
                  >
                    Visit OthelloPawcare.com
                  </a>
                </div>

              </div>
            </FadeIn>
          </article>
        </div>
      );
    }
    if (slug === 'squad-agency') {
      return (
        <div className="pt-32 pb-24 min-h-screen animate-in fade-in duration-500 container mx-auto px-6 md:px-12">
          <SEO 
            title="Squad Agency UI/UX Case Study | Rayrona Studio"
            description="Deep dive into the visual design and UX architecture for Squad, a leading Australian web and marketing agency."
            url="https://rayronastudio.com/work/squad-agency"
            type="article"
          />
          <FadeIn>
            <Link
              to="/"
              onClick={() => setTimeout(() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' }), 100)}
              className="flex items-center gap-2 text-zinc-400 hover:text-accent transition-colors mb-12 font-medium w-fit"
            >
              <ArrowLeft size={18} /> Back to Work
            </Link>
          </FadeIn>

          <article className="max-w-4xl mx-auto">
            <FadeIn delay={100}>
              <p className="text-accent font-medium tracking-widest uppercase text-sm mb-4">UI/UX Design & Design System</p>
              <h1 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">Squad <span className="font-georgia text-accent font-normal">Agency</span></h1>
              <div className="w-full rounded-3xl overflow-hidden mb-16 shadow-2xl flex items-center justify-center p-12 bg-gradient-to-br from-gray-900 to-black">
                <img src="/squad.png" alt="Squad Agency Laptop Mockup" loading="lazy" decoding="async" className="w-full max-w-4xl object-contain transform hover:scale-105 transition-transform duration-700" />
              </div>
            </FadeIn>

            <FadeIn delay={200}>
              <div className="prose prose-lg text-gray-300 leading-relaxed font-dmsans max-w-none">
                
                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Project Overview & The Business Challenge</h2>
                <p className="mb-6">Squad is a premier web design and search marketing (SEO) agency based in Australia. Operating in a highly competitive B2B market, their digital presence needed to communicate absolute authority, modern technical prowess, and an approachable, results-driven ethos. The Australian digital marketing landscape is saturated with generic, template-driven websites that fail to inspire confidence in enterprise-level clients. Squad required a digital platform that would not just list their services, but actively demonstrate their expertise through an impeccable user experience.</p>
                <p className="mb-6">As an agency that sells high-end 'Web Design' and 'SEO' to corporate clients, their own website is their ultimate portfolio piece. It cannot simply tell potential clients that they are the best; it must unequivocally <em>show</em> them. The challenge was to balance the complex, data-heavy requirements of an SEO agency with the sleek, emotional resonance of a top-tier design studio. It needed to be lightning-fast to satisfy search engine algorithms, yet visually rich enough to stop a marketing director in their tracks.</p>
                <p className="mb-12">I currently work at Squad Agency, and for this specific flagship project, I was responsible strictly for the <strong>UI/UX Design</strong>, the user journey architecture, and the creation of a comprehensive, production-ready Figma Design System. My primary objective was to craft a sophisticated visual language that the engineering team could seamlessly translate into a high-performance, accessible, and scalable web application.</p>

                <div className="bg-zinc-900/50 p-10 rounded-3xl border border-zinc-800 mb-16">
                  <h3 className="text-2xl font-bold text-white mb-6">My Role: UI/UX Architect</h3>
                  <ul className="list-disc pl-6 space-y-3 text-lg">
                    <li><strong>Strategic Redesign:</strong> Led the visual redesign of the entire corporate website, moving from a legacy layout to a modern, component-driven architecture.</li>
                    <li><strong>Design System Engineering:</strong> Engineered a highly scalable, atomic Figma component library serving as the single source of truth for the engineering team.</li>
                    <li><strong>Conversion Optimization:</strong> Designed responsive, mobile-first B2B conversion funnels focused on reducing friction in the lead-generation process.</li>
                    <li><strong>Visual Identity & Motion:</strong> Created custom vector assets, established a modern typographic hierarchy, and defined the motion design guidelines for micro-interactions.</li>
                  </ul>
                </div>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Design Philosophy: Technical Elegance & The Dark Mode "Vibe"</h2>
                <p className="mb-6">We adopted a design philosophy I call "Technical Elegance". This approach strips away unnecessary ornamentation while elevating the core content through dramatic lighting, precise typography, and purposeful negative space. In the B2B tech space, trust is often established subconsciously within the first three seconds of page load. If the design feels outdated or cluttered, the user immediately assumes the agency's technical capabilities are similarly lagging.</p>
                <p className="mb-6">To achieve a striking, premium feel, we utilized a stark, high-contrast dark mode aesthetic for the primary presentation assets. This deep, almost infinite dark background serves as a cinematic canvas, allowing the vibrant, futuristic brand colors—specifically electric cyan and deep magenta gradients—to visually pop and draw the user's eye to key calls to action. The dark mode isn't just an aesthetic choice; it communicates a sense of advanced technology, coding environments, and modern digital mastery. It feels sleek, mysterious, and highly professional.</p>
                <p className="mb-6">However, to ensure readability for long-form case studies and technical SEO documentation, the core UI of the site leverages vast amounts of intentional whitespace (or rather, 'dark space'), crisp sans-serif typography, and subtle glassmorphism effects. We paired the mechanical, highly geometric precision of Inter for our UI elements with the slightly softer, humanist warmth of DM Sans for long-form reading. These translucent, blurred backgrounds create a sense of depth and hierarchy without introducing visual clutter, resulting in an interface that feels both expansive and intimately focused.</p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Information Architecture & Conversion Funnels</h2>
                <p className="mb-6">A beautiful site that fails to convert is a failed design. B2B clients don't just browse casually; they arrive with specific pain points, tight budgets, and need immediate reassurance that Squad can solve their unique problems. I completely restructured the site's information architecture to follow a psychological and logical narrative: Hook the user with stunning visuals, establish Authority through data, explain the Methodology, provide social Proof, and finally, drive Action.</p>
                <p className="mb-6">The main navigation was significantly simplified, grouping complex, multi-tiered service offerings into intuitive pillars. We designed specific landing pages for SEO, Web Design, and Paid Media that didn't just dump walls of text. Instead, they guided the user through interactive data visualizations, clear client success metrics, and tangible ROIs. We wanted the user to feel the value before they even picked up the phone.</p>
                <p className="mb-6">The "Let's Talk" lead generation flow was completely reimagined. Instead of an intimidating, 15-field contact form that screams "bureaucracy", we created a multi-step, conversational interface. It gently qualifies leads by asking one question at a time—budget, project type, timeline—accompanied by smooth slide transitions. This approach dramatically reduces cognitive load and makes the inquiry process feel less like an interrogation and more like the beginning of a collaborative partnership.</p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Engineering the Figma Design System</h2>
                <p className="mb-6">Because I was handing off this complex design to our highly skilled internal engineering team, a standard flat mockup was completely insufficient. Developers need logic, rules, and constraints, not just static pictures. I built a robust, atomic Design System within Figma, constructed entirely upon auto-layout, global color tokens, and deeply nested master components.</p>
                <p className="mb-6">Every single interactive element was meticulously documented. Buttons weren't just drawn; they were defined with default, hover, active, focused, and disabled states. Input fields accounted for error states, success validation, and placeholder contrast ratios to ensure accessibility. The entire layout adhered strictly to an 8pt baseline grid system. This mathematical approach to spacing ensures vertical rhythm and consistent alignment across every conceivable viewport, from a 4K monitor down to an iPhone SE.</p>
                <p className="mb-6">This systematic, token-based approach ensured that when the developers began writing the React components and configuring Tailwind CSS, they had a definitive single source of truth. It eliminated the dreaded "pixel-pushing" phase of development, allowing the engineers to focus their cognitive energy on complex state management, API integrations, and performance optimization rather than guessing margin values or hex codes.</p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">Micro-interactions and the Living Interface</h2>
                <p className="mb-6">Static designs belong in print magazines. A modern digital interface must feel alive, breathing, and responsive to the user's touch. I integrated comprehensive motion design guidelines directly into the handoff documentation. This included specifying the exact bezier curve values for spring animations, the millisecond duration of hover states on project cards, and the subtle parallax scrolling effects that give the site a sense of cinematic depth.</p>
                <p className="mb-6">When a user hovers over a portfolio case study card, it doesn't just change color abruptly; it subtly lifts off the page with an expanded, colored drop shadow, acknowledging their intent with a tactile response. Page transitions were choreographed to feel seamless and fluid, avoiding the jarring flash of white typical of traditional multi-page applications. These micro-interactions are the invisible glue that holds the entire "vibe" together. They transform a functionally good website into a premium, memorable digital experience that users actively enjoy interacting with.</p>

                <h2 className="text-3xl font-bold text-white mt-12 mb-6">The Hand-off & Cross-Disciplinary Collaboration</h2>
                <p className="mb-6">A successful UI/UX Designer doesn't just make pretty pictures and throw them over the fence to the development team; they solve structural problems and facilitate smooth engineering. During the crucial hand-off phase, I worked side-by-side with the front-end engineers in daily standups to ensure the designed animations were actually feasible within our strict performance budgets.</p>
                <p className="mb-6">We leveraged Figma's Dev Mode heavily to export exact CSS values, transition timings, and optimized SVG assets directly into the codebase. When a complex background blur effect caused rendering lag on lower-end mobile devices during prototyping, we collaborated on the spot to find a performant CSS alternative. We maintained the aesthetic intent of the glassmorphism without sacrificing the critical 60 frames-per-second scrolling experience. This mutual respect and constant feedback loop between design and engineering is what ultimately guarantees the quality and polish of the final product.</p>
                <p className="mb-6">The result is a stunning, high-converting digital storefront that perfectly encapsulates Squad's position as a dominant leader in the Australian digital marketing space. It is a testament to the power of combining emotional "vibe design" with rigorous, uncompromising technical architecture.</p>

                <div className="mt-16 pt-12 border-t border-zinc-800 text-center">
                  <h3 className="text-2xl font-bold text-white mb-6">Experience the Live Design</h3>
                  <a 
                    href="https://squadagency.com.au/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-block bg-accent hover-bg-accent text-white px-8 py-4 rounded-full font-medium transition-all duration-300 transform hover:-translate-y-1 shadow-lg shadow-orange-500/20"
                  >
                    Visit SquadAgency.com.au
                  </a>
                </div>

              </div>
            </FadeIn>
          </article>
        </div>
      );
    }
    
    // Fallback for other projects without case studies yet
    const project = PORTFOLIO_ITEMS.find(p => p.slug === slug);
    return (
      <div className="pt-32 text-center min-h-screen">
        <h1 className="text-4xl font-bold mb-4">{project ? project.title : 'Project Not Found'}</h1>
        <p className="mb-8">Case study coming soon.</p>
        <Link to="/" onClick={() => setTimeout(() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="text-accent underline">Back to Home</Link>
      </div>
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: globalStyles }} />

      <div className="min-h-screen flex flex-col relative selection:bg-accent selection:text-white">

        {/* Navigation */}
        <header className="fixed w-full z-50 top-0 bg-black/80 backdrop-blur-md border-b border-zinc-900 transition-all duration-300">
        <nav aria-label="Main Navigation" className="w-full">
          <div className="container mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              className="text-2xl font-bold cursor-pointer flex items-center gap-3 group font-syne"
            >
              <img src="/favicon.svg" alt="Rayrona Studio Logo" width="32" height="32" fetchpriority="high" className="w-8 h-8 transition-transform duration-300 group-hover:scale-105" />
              <span>Rayrona <span className="text-accent font-syne font-normal ml-1">Studio</span></span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8 font-medium">
              <Link
                to="/"
                onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`hover:text-accent transition-colors ${currentPage === 'home' ? 'text-accent' : 'text-white'}`}
              >
                Home
              </Link>
              <button
                onClick={() => {
                  navigateTo('home');
                  setTimeout(() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }}
                className="hover:text-accent transition-colors text-white"
              >
                About
              </button>
              <button
                onClick={() => {
                  navigateTo('home');
                  setTimeout(() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }}
                className="hover:text-accent transition-colors text-white"
              >
                Services
              </button>
              <button
                onClick={() => {
                  navigateTo('home');
                  setTimeout(() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }}
                className="hover:text-accent transition-colors text-white"
              >
                Work
              </button>
              <Link
                to="/blog"
                className={`hover:text-accent transition-colors ${currentPage === 'blog' || currentPage === 'post' ? 'text-accent' : 'text-white'}`}
              >
                Blog
              </Link>
              <a href="https://mail.google.com/mail/?view=cm&fs=1&to=codeveloper16@gmail.com" target="_blank" rel="noopener noreferrer" className="bg-white text-black px-5 py-2 rounded-full hover:bg-accent hover:text-white transition-all text-sm font-semibold">
                Let's Talk
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              type="button"
              aria-label="Toggle Navigation Menu"
              className="md:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="md:hidden absolute top-20 left-0 w-full bg-zinc-950 border-b border-zinc-900 shadow-2xl py-6 px-6 flex flex-col gap-6 animate-in slide-in-from-top-4">
              <Link
                to="/"
                onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setIsMobileMenuOpen(false); }}
                className="text-left text-2xl font-bold hover:text-accent block text-white"
              >
                Home
              </Link>
              <button
                onClick={() => {
                  navigateTo('home');
                  setTimeout(() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }), 100);
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-2xl font-bold hover:text-accent text-white"
              >
                About
              </button>
              <button
                onClick={() => {
                  navigateTo('home');
                  setTimeout(() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }), 100);
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-2xl font-bold hover:text-accent text-white"
              >
                Services
              </button>
              <button
                onClick={() => {
                  navigateTo('home');
                  setTimeout(() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' }), 100);
                  setIsMobileMenuOpen(false);
                }}
                className="text-left text-2xl font-bold hover:text-accent text-white"
              >
                Work
              </button>
              <Link
                to="/blog"
                onClick={() => { setIsMobileMenuOpen(false); }}
                className="text-left text-2xl font-bold hover:text-accent block text-white"
              >
                Blog
              </Link>
              <a href="https://mail.google.com/mail/?view=cm&fs=1&to=codeveloper16@gmail.com" target="_blank" rel="noopener noreferrer" className="text-left text-xl text-accent font-medium mt-4">
                codeveloper16@gmail.com
              </a>
            </div>
          )}
        </nav>
        </header>

        {/* Dynamic Page Rendering */}
        <main id="main-content" className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy" element={<PrivacyList />} />
            <Route path="/privacy/:id" element={<PrivacyDetail />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/work/:slug" element={<CaseStudy />} />
          </Routes>
        </main>

        {/* Modern Footer */}
        <footer className="bg-[#0a0a0a] text-white pt-24 pb-8 mt-auto relative overflow-hidden">
          {/* Latar Belakang Dekoratif Opsional */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-20"></div>

          <div className="container mx-auto px-6 md:px-12">

            {/* Call to Action Raksasa */}
            <div className="mb-24 flex flex-col items-center text-center">
              <h2 className="text-6xl md:text-8xl lg:text-[10rem] font-bold font-syne leading-none tracking-tighter mb-8 hover:text-accent transition-colors duration-700 cursor-default">
                LET'S <span className="font-georgia italic font-normal text-accent">TALK</span>
              </h2>
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=codeveloper16@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 text-xl md:text-3xl font-dmsans border-b-2 border-transparent hover:border-accent pb-1 transition-all group"
              >
                codeveloper16@gmail.com
                <ArrowRight size={28} className="text-accent transform group-hover:translate-x-2 transition-transform duration-300" />
              </a>
            </div>

            {/* Grid Informasi & Navigasi */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 py-16 border-t border-white/10">
              <div className="md:col-span-5">
                <h3 className="text-3xl font-bold font-syne mb-6 flex items-center gap-3">
                  <img src="/favicon.svg" alt="Rayrona Studio Logo" width="40" height="40" loading="lazy" decoding="async" className="w-10 h-10" />
                  <span>Rayrona <span className="text-accent font-syne font-normal ml-1">Studio</span></span>
                </h3>
                <p className="text-gray-400 font-dmsans max-w-sm leading-relaxed text-lg">
                  Designing the vibe, coding the future. Building digital experiences that don't just work, but feel right.
                </p>
              </div>

              <div className="md:col-span-3 flex flex-col gap-4">
                <h4 className="text-sm font-bold tracking-widest text-gray-400 uppercase mb-4">Navigation</h4>
                <Link to="/" onClick={() => setTimeout(() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="text-left text-lg text-gray-300 hover:text-white hover:translate-x-1 transition-all w-fit block">Work</Link>
                <Link to="/blog" className="text-left text-lg text-gray-300 hover:text-white hover:translate-x-1 transition-all w-fit block">Blog</Link>
                <Link to="/privacy" className="text-left text-lg text-gray-300 hover:text-white hover:translate-x-1 transition-all w-fit block">Privacy Policy</Link>
                <Link to="/delete-account" className="text-left text-lg text-gray-300 hover:text-white hover:translate-x-1 transition-all w-fit block">Delete Account</Link>
              </div>

              <div className="md:col-span-4 flex flex-col gap-4">
                <h4 className="text-sm font-bold tracking-widest text-gray-400 uppercase mb-4">Social Media</h4>
                <div className="flex gap-4">
                  <a href="https://www.instagram.com/rayronae/" target="_blank" rel="noopener noreferrer" aria-label="Instagram Profile" className="p-4 bg-white/5 rounded-full hover:bg-accent hover:-translate-y-2 transition-all duration-300 text-gray-300 hover:text-white"><FaInstagram size={22} /></a>
                  <a href="https://www.linkedin.com/in/rayrona/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Profile" className="p-4 bg-white/5 rounded-full hover:bg-accent hover:-translate-y-2 transition-all duration-300 text-gray-300 hover:text-white"><FaLinkedin size={22} /></a>
                  <a href="https://upwork.com/freelancers/rayrona" target="_blank" rel="noopener noreferrer" aria-label="Upwork Profile" className="p-4 bg-white/5 rounded-full hover:bg-accent hover:-translate-y-2 transition-all duration-300 text-gray-300 hover:text-white"><SiUpwork size={22} /></a>
                  <a href="https://dribbble.com/rayrona" target="_blank" rel="noopener noreferrer" aria-label="Dribbble Profile" className="p-4 bg-white/5 rounded-full hover:bg-accent hover:-translate-y-2 transition-all duration-300 text-gray-300 hover:text-white"><FaDribbble size={22} /></a>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/10 text-sm text-gray-400 font-dmsans">
              <p>© {new Date().getFullYear()} Rayrona Studio. All rights reserved.</p>

              {/* Status Indikator Modern */}
              <div className="mt-6 md:mt-0 flex items-center gap-3 bg-white/5 px-4 py-2 rounded-full border border-white/10">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
                <span className="text-gray-300 text-xs uppercase tracking-wider font-bold">Available for projects</span>
              </div>
            </div>

          </div>
        </footer>

      </div>
    </>
  );
}
